import { AutoComplete, type Option } from '@lms/ui/autocomplete';
import { Input } from '@lms/ui/input';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslations } from 'use-intl';
import { SubmitButton } from '@/components/submit-button';
import type { Site } from '@/features/branding/brand-provider';
import { searchGroups, submitCode } from '@/features/student/student-api';
import { GroupSelector } from './group-selector';

type Phase =
  | { kind: 'code' }
  | {
      kind: 'select_group';
      groups: { id: string; name: string }[];
      groupType: 'tenant' | 'supplier';
    }
  | { kind: 'enter_supplier' };

export default function CodeForm({
  className,
  site,
}: {
  className?: string;
  site: Site;
}) {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const t = useTranslations();
  const codeParam = params.get('code')?.trim() ?? '';
  const [code, setCode] = useState(codeParam);
  const [phase, setPhase] = useState<Phase>({ kind: 'code' });
  const [error, setError] = useState<string | undefined>(undefined);
  const [pending, setPending] = useState(false);
  const [group, setGroup] = useState('');
  const [supplier, setSupplier] = useState<Option>({ value: '', label: '' });

  const run = useCallback(
    async (groupId?: number, groupName?: string, replace = false) => {
      setPending(true);
      try {
        const result = await submitCode({
          slug: site.slug,
          code,
          groupId,
          groupName,
        });
        switch (result.kind) {
          case 'redirect':
            navigate(result.to, { replace });
            break;
          case 'select_group':
            setError(undefined);
            setPhase({
              kind: 'select_group',
              groups: result.groups,
              groupType: result.groupType,
            });
            break;
          case 'enter_supplier':
            setError(undefined);
            setPhase({ kind: 'enter_supplier' });
            break;
          case 'error':
            setError(t(`login.${result.message}`));
            setPhase({ kind: 'code' });
            break;
        }
      } finally {
        setPending(false);
      }
    },
    [code, navigate, site.slug, t],
  );

  // QR-code links carry ?code=; submit it straight away instead of making the
  // user press continue. The ref keeps StrictMode's double effect from
  // redeeming a shared code twice, and replacing the history entry keeps the
  // back button from landing here and redeeming it again. If the code needs a
  // group or is invalid, the matching form is shown as usual.
  const autoSubmitted = useRef(false);
  useEffect(() => {
    if (!codeParam || autoSubmitted.current) return;
    autoSubmitted.current = true;
    run(undefined, undefined, true);
  }, [codeParam, run]);

  // Select a specific group from list
  if (phase.kind === 'select_group') {
    return (
      <div className={className} data-testid="select-group-form">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            run(group ? Number.parseInt(group, 10) : undefined);
          }}
        >
          <input type="hidden" value={code} name="code" />
          <p>
            {t(`login.select_${phase.groupType}`, {
              alias: t(`group_alias.${site.groupAlias}_singular`).toLowerCase(),
            })}
          </p>
          <GroupSelector
            placeholder={
              phase.groupType === 'supplier'
                ? t('group_alias.suppliers_singular')
                : t(`group_alias.${site.groupAlias}_singular`)
            }
            groups={phase.groups.map((g) => ({ label: g.name, value: g.id }))}
            emptyString={t('login.nothing_found')}
            value={group}
            onChange={setGroup}
          />
          {error && <div className="text-red-500">{error}</div>}
          <SubmitButton
            label={t('login.continue')}
            className="mt-4"
            pending={pending}
          />
        </form>
      </div>
    );
  }

  // Enter/search for company name
  if (phase.kind === 'enter_supplier') {
    return (
      <div className={className} data-testid="enter-supplier-form">
        <EnterGroup
          groupAlias={site.groupAlias}
          locationId={site.id}
          value={supplier}
          onValueChange={setSupplier}
          error={error}
          pending={pending}
          onSubmit={() =>
            run(
              supplier.value ? Number.parseInt(supplier.value, 10) : undefined,
              supplier.label || undefined,
            )
          }
        />
      </div>
    );
  }

  // Enter code, first part
  return (
    <div className={className} data-testid="code-form">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          run();
        }}
      >
        <p>{t('login.enter_code')}</p>
        <Input
          type="text"
          name="code"
          value={code}
          onChange={(v) => setCode(v.target.value)}
          className="input input-bordered mt-4 w-full"
          data-testid="code-input"
        />
        {error && (
          <div className="text-red-500" data-testid="code-error">
            {error}
          </div>
        )}
        <SubmitButton
          label={t('login.continue')}
          className="mt-4"
          disabled={code.length === 0}
          pending={pending}
        />
      </form>
    </div>
  );
}

function EnterGroup({
  groupAlias,
  locationId,
  value,
  onValueChange,
  onSubmit,
  error,
  pending,
}: {
  groupAlias: Site['groupAlias'];
  locationId: number;
  value: Option;
  onValueChange: (option: Option) => void;
  onSubmit: () => void;
  error?: string;
  pending: boolean;
}) {
  const t = useTranslations();
  const [searchResults, setSearchResults] = useState<Option[]>([]);
  const [isSearching, setIsSearching] = useState(false);

  const search = useCallback(
    (query: string) => {
      if (!query) {
        setSearchResults([]);
        return;
      }

      setIsSearching(true);
      searchGroups(locationId, query)
        .then((groups) => {
          setSearchResults(groups);
        })
        .finally(() => setIsSearching(false));
    },
    [locationId],
  );

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
    >
      <p>
        {t('login.enter_group', {
          alias: t(`group_alias.${groupAlias}_singular`).toLowerCase(),
        })}
      </p>
      <AutoComplete
        name="groupName"
        value={value}
        isLoading={isSearching}
        onValueChange={onValueChange}
        search={search}
        options={searchResults}
      />
      {error && <div className="text-red-500">{error}</div>}
      <SubmitButton
        label={t('login.continue')}
        className="mt-4"
        pending={pending}
      />
    </form>
  );
}
